import { EcfVec3, EciVec3, LookAngles } from 'satellite.js';

import { Satellite, Station, StationPosition, Tle } from '@app/store/gnssm/codegen/gnssmApi';

import { DateTimeInterval } from '@app/utils/Datetime';

/// enums

/**
 * Enum representing the GNSS which are monitored.
 * This is a smaller set than the type defined in the REST API.
 */
export enum GNSS {
  Galileo = 'galileo',
  GPS = 'gps',
  Glonass = 'glonass',
  Beidou = 'beidou',
  QZSS = 'qzss',
  Navic = 'navic',
  Sbas = 'sbas',
}

export enum TEC {
  measurement = 'tec-measurements',
  model = 'tec-model',
  error = 'tec-error',
  roti = 'roti',
}

export enum HealthStatus {
  Healthy = 'healthy',
  Marginal = 'marginal',
  Unhealthy = 'unhealthy',
}

export enum StationType {
  Real,
  Custom,
}

export enum DOPType {
  GDOP = 'Geometric',
  PDOP = 'Position',
  HDOP = 'Horizontal',
  VDOP = 'Vertical',
  TDOP = 'Time',
}

export enum StationSelection {
  None = 'None',
  All = 'All IGS stations',
  PerformanceAnalysis = 'Stations for performance analysis',
}

export enum SatelliteSelection {
  None = 'None',
  All = 'All',
  FromStation = 'From Station',
  OnlySelected = 'Only Selected',
}

/// Types

export interface Position {
  latitude: number;
  longitude: number;
  height?: number;
}

export type SatellitePosition = {
  noradId: number;
  timestamp: Date;
  eci_position: EciVec3<number>;
  map_position: Position;
};

export type SatelliteNavigation = {
  satellite: Satellite;
  status: HealthStatus;
  position: SatellitePosition;
};

export type GroundStation = Station &
  StationPosition & {
    type: StationType;
    label: string;
  };

export type CustomStation = {
  code: string;
  type: StationType;
  label: string;
  latitude: number;
  longitude: number;
  height: number;
};

export interface Coordinates {
  latitude?: number;
  longitude?: number;
  height?: number;
  elevation?: number;
}

export type TlesPerInterval = {
  interval: DateTimeInterval<string>;
  tles: Tle[];
};

export type SatelliteStationVisibility = SatellitePosition & {
  noradId: number;
  /// station from which satellite is viewed
  stationCode: string;
  /// lat, long, height of the station
  stationPosition: Position;
  /// look angles of the satellite relative to the station
  lookAngles: LookAngles;
  /// satellite position in ecf coordinates
  ecfPosition: EcfVec3<number>;
  /// visible from station: true / false
  isVisible: boolean;
};

export type TecQuality = 'normal' | 'low' | undefined;
